import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LogisticsCargoCandidate } from '@magimp/shared';
import { formatDateTime } from '../../../i18n/index.js';
import { Button, Combobox, FormRow } from '../../../ui/index.js';
import { useLogisticsCargoCandidates } from '../queries.js';

/**
 * The document another draft is holding a cargo for, or nothing when this
 * document may still take it.
 *
 * WHY the open document is not a holder: a manager who removed the row locally
 * has to be able to put it back before saving, and the save that follows
 * rewrites the same hold.
 */
function holderOf(
  candidate: LogisticsCargoCandidate,
  openCalculationId: string | undefined,
): string | null {
  if (!candidate.heldBy || candidate.heldBy.calculationId === openCalculationId) return null;
  return candidate.heldBy.publicCode;
}

export function CargoPicker({
  excludedCandidateIds,
  openCalculationId,
  onConfirm,
}: {
  excludedCandidateIds: ReadonlySet<string>;
  openCalculationId?: string | undefined;
  onConfirm: (candidate: LogisticsCargoCandidate) => boolean;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState<LogisticsCargoCandidate | null>(null);
  const candidates = useLogisticsCargoCandidates(query, active);
  const items = (candidates.data?.items ?? []).filter(
    (candidate) => !excludedCandidateIds.has(candidate.id),
  );

  const close = () => {
    setOpen(false);
    setActive(false);
    setQuery('');
    setSelected(null);
  };

  if (!open) {
    return (
      <Button
        kind="secondary"
        icon="plus"
        onClick={() => {
          setOpen(true);
          setActive(true);
        }}
      >
        {t('editor.cargoPicker.choose')}
      </Button>
    );
  }

  if (selected) {
    const holder = holderOf(selected, openCalculationId);
    return (
      <div className="rl-cargo-picker-preview" data-testid="cargo-candidate-preview">
        <dl className="rl-detail-grid">
          <PreviewField label={t('editor.cargo.article')} value={selected.article} />
          <PreviewField label={t('editor.cargo.name')} value={selected.name} />
          <PreviewField label={t('editor.cargo.seats')} value={selected.seats} />
          <PreviewField label={t('editor.cargo.weight')} value={selected.weight} />
          <PreviewField label={t('editor.cargo.volume')} value={selected.volume} />
          <PreviewField
            label={t('editor.cargo.departedAt')}
            value={formatDateTime(selected.departedAt)}
          />
          <PreviewField
            label={t('editor.cargo.arrivedAt')}
            value={formatDateTime(selected.arrivedAt)}
          />
          <PreviewField label={t('editor.cargo.transportType')} value={selected.transportType} />
        </dl>
        {holder ? (
          <p className="rl-cargo-picker-held" role="alert">
            {t('editor.cargoPicker.heldMessage', { code: holder })}
          </p>
        ) : null}
        <div className="rl-cargo-picker-actions">
          <Button kind="ghost" onClick={close}>
            {t('actions.cancel')}
          </Button>
          <Button
            kind="primary"
            disabled={holder !== null}
            onClick={() => {
              if (onConfirm(selected)) close();
            }}
          >
            {t('editor.cargoPicker.add')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormRow label={t('editor.cargoPicker.label')}>
      <Combobox
        value={null}
        loading={candidates.isFetching}
        placeholder={t('editor.cargoPicker.placeholder')}
        ariaLabel={t('editor.cargoPicker.label')}
        onQueryChange={(value) => {
          setActive(true);
          setQuery(value);
        }}
        // The shared option carries a label and nothing else, so a cargo another
        // document holds says so in the label and is refused at the preview
        // below rather than being listed as ordinarily available.
        options={items.map((candidate) => {
          const holder = holderOf(candidate, openCalculationId);
          return {
            value: candidate.id,
            label: [
              `${candidate.article}${candidate.name ? ` — ${candidate.name}` : ''}`,
              ...(holder ? [t('editor.cargoPicker.heldOption', { code: holder })] : []),
            ].join(' · '),
          };
        })}
        onChange={(id) => {
          setSelected(items.find((candidate) => candidate.id === id) ?? null);
          setQuery('');
        }}
      />
    </FormRow>
  );
}

function PreviewField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}
